import React, { useState } from "react";
import {
  maintenanceApi,
  VehicleModel,
  VehicleCompanyOption,
  VehicleTypeOption,
  VehicleSocketType,
} from "../../../api/maintenance";
import { X } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface Props {
  companies: VehicleCompanyOption[];
  vehicleTypes: VehicleTypeOption[];
  socketTypes: VehicleSocketType[];
  editingModel: VehicleModel | null;
  defaultCompanyId?: number;
  onClose: () => void;
  onDone: () => void;
}

const VehicleModelForm: React.FC<Props> = ({
  companies, vehicleTypes, socketTypes, editingModel, defaultCompanyId, onClose, onDone,
}) => {
  const isEdit = !!editingModel;

  const [companyId, setCompanyId] = useState<number | "">(
    editingModel?.vehicle_company_id || defaultCompanyId || ""
  );
  const [modelName, setModelName] = useState(editingModel?.model_name || "");
  const [vehicleType, setVehicleType] = useState<number | "">(editingModel?.vehicle_type || "");
  const [batteryChemistry, setBatteryChemistry] = useState(editingModel?.battery_chemistry || "");
  const [ratedCapacity, setRatedCapacity] = useState(
    editingModel?.rated_capacity_kwh?.toString() || ""
  );
  const [range, setRange] = useState(
    editingModel?.claimed_full_charge_range?.toString() || ""
  );
  const [socketTypeId, setSocketTypeId] = useState<number | "">(
    editingModel?.charging_socket_type_id || ""
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useNotice("error", "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !modelName.trim() || !vehicleType || !batteryChemistry.trim() ||
        !ratedCapacity || !range || !socketTypeId) {
      setError("All fields are required");
      return;
    }
    setSubmitting(true);
    setError("");

    const payload = {
      vehicle_company_id: companyId as number,
      model_name: modelName.trim(),
      vehicle_type: vehicleType as number,
      battery_chemistry: batteryChemistry.trim(),
      rated_capacity_kwh: parseInt(ratedCapacity, 10),
      claimed_full_charge_range: parseInt(range, 10),
      charging_socket_type_id: socketTypeId as number,
    };

    try {
      if (isEdit && editingModel) {
        await maintenanceApi.updateVehicleModel(editingModel.id, payload);
      } else {
        await maintenanceApi.createVehicleModel(payload);
      }
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save model");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="maint-form-overlay">
      <div className="maint-form-card maint-form-card--wide">
        <div className="maint-form-header">
          <h3>{isEdit ? "Edit" : "Add"} Vehicle Model</h3>
          <button onClick={onClose} className="maint-btn-icon"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="maint-form-row">
            <label>
              Company *
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : "")}
                required
              >
                <option value="">Select company...</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label>
              Model name *
              <input
                type="text"
                value={modelName}
                maxLength={1024}
                placeholder="e.g. Nexon EV"
                onChange={(e) => setModelName(e.target.value)}
                required
              />
            </label>
          </div>

          <div className="maint-form-row">
            <label>
              Vehicle type *
              <select
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value ? Number(e.target.value) : "")}
                required
              >
                <option value="">Select type...</option>
                {vehicleTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.type_name}</option>
                ))}
              </select>
            </label>
            <label>
              Charging socket *
              <select
                value={socketTypeId}
                onChange={(e) => setSocketTypeId(e.target.value ? Number(e.target.value) : "")}
                required
              >
                <option value="">Select socket...</option>
                {socketTypes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.socket_name}{s.charging_current_mode ? ` (${s.charging_current_mode})` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="maint-form-row">
            <label>
              Battery chemistry *
              <input
                type="text"
                value={batteryChemistry}
                maxLength={256}
                placeholder="e.g. LFP, NMC"
                onChange={(e) => setBatteryChemistry(e.target.value)}
                required
              />
            </label>
            <label>
              Rated capacity (kWh) *
              <input
                type="number"
                min="0"
                step="1"
                value={ratedCapacity}
                onChange={(e) => setRatedCapacity(e.target.value)}
                required
              />
            </label>
          </div>

          <label>
            Claimed full-charge range (km) *
            <input
              type="number"
              min="0"
              step="1"
              value={range}
              onChange={(e) => setRange(e.target.value)}
              required
            />
          </label>

          {error && <p className="maint-form-error">{error}</p>}
          <div className="maint-form-actions">
            <button type="button" onClick={onClose} className="maint-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="maint-btn-primary">
              {submitting ? "Saving..." : isEdit ? "Update" : "Create"} Model
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleModelForm;
